import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ReportDTOSchema, ReviewStatus } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const DoctorReportRowSchema = z.object({
  report: ReportDTOSchema,
  consultation: z.object({
    id: z.string(),
    patientName: z.string(),
    doctorId: z.string(),
    language: z.string(),
    startedAt: z.string(),
    patientId: z.string().nullable(),
  }),
});

export type DoctorReportRow = z.infer<typeof DoctorReportRowSchema>;
export type DoctorReportStatusFilter = "pending_audit" | "approved" | "revision_requested" | "not_submitted" | "all";

const DoctorReportListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(DoctorReportRowSchema),
});

export function useDoctorReports(status: DoctorReportStatusFilter = "all") {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["doctor-reports", status],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/doctor/reports?status=${status}&pageSize=100`,
      });
      return DoctorReportListResponseSchema.parse(json).data;
    },
  });
}

export function useDoctorPendingCount() {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["doctor-pending-count"],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/doctor/reports/pending-count",
      }) as { success: boolean; data: { count: number } };
      return json.data.count;
    },
    refetchInterval: 60_000,
  });
}

const ReportResponseSchema = z.object({
  success: z.boolean(),
  data: ReportDTOSchema.nullable(),
});

export function useReport(
  id: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const backend = useBackendAuthenticated();
  const enabled = options.enabled !== undefined ? options.enabled : !!id;
  return useQuery({
    queryKey: ["report", id],
    queryFn: async () => {
      try {
        const json = await backend({
          method: "GET",
          url: `/api/v1/consultations/${id}/report`,
        });
        return ReportResponseSchema.parse(json).data;
      } catch {
        return null;
      }
    },
    retry: false,
    enabled,
  });
}

export function useGenerateReport(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/report`,
        data: { text },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["report", id] }),
  });
}

export function usePatchReport(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      editedData?: Record<string, unknown>;
      generalEditedData?: Record<string, unknown>;
      reviewStatus?: ReviewStatus;
      doctorNotes?: string;
      narrativeEditedData?: Record<string, string>;
    }) =>
      backend({
        method: "PATCH",
        url: `/api/v1/consultations/${id}/report`,
        data: payload,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["report", id] }),
  });
}

/** Re-renders the report text from the answers as they stand now. */
export function useRegenerateNarrative(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: () =>
      backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/report/narrative`,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report", id] });
      toast.success(t("report.narrativeRegenerated"));
    },
    onError: () => toast.error(t("report.narrativeRegenerateError")),
  });
}

export function useLinkReport(consultationId: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (linkedReportId: string | null) => {
      return backend({ method: "PATCH", url: `/api/v1/consultations/${consultationId}/report/link`, data: { linkedReportId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report", consultationId] });
    },
  });
}

export function useExportReportPdf(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (language: string) => {
      const blob = await backend<Blob>({
        method: "GET",
        url: `/api/v1/consultations/${id}/report/pdf?lang=${language}`,
        responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-${id?.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error(t("report.exportError")),
  });
}

// ─── Primary visit ────────────────────────────────────────────────

/**
 * The doctor's diagnosis. Also refills the plan from that diagnosis's clinical
 * protocol when a protocol is passed, which is why the report is refetched
 * rather than patched locally.
 */
export function useSetDiagnosis(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (payload: {
      diagnosisMain: string;
      diagnosisIcd10?: string | null;
      diagnosisConcomitant?: string | null;
      diagnosisComplications?: string | null;
      protocolId?: string | null;
    }) =>
      backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/report/diagnosis`,
        data: payload,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report", id] });
      toast.success(t("primaryVisit.diagnosisSaved"));
    },
    onError: () => toast.error(t("primaryVisit.diagnosisError")),
  });
}

/** The doctor signing off sections the system filled in. */
export function useConfirmSections(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (sections: string[]) =>
      backend({
        method: "POST",
        url: `/api/v1/consultations/${id}/report/confirm`,
        data: { sections },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report", id] });
      toast.success(t("primaryVisit.sectionConfirmed"));
    },
    onError: () => toast.error(t("primaryVisit.confirmError")),
  });
}
